import React, { useState, useEffect, useRef } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import axios from "axios";
import { 
  FaGraduationCap, 
  FaGlobe, 
  FaTrophy, 
  FaRocket, 
  FaCheckCircle,
  FaArrowRight,
  FaStar,
  FaHandshake,
  FaLightbulb,
  FaUsers,
  FaAward,
  FaChartLine,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt
} from "react-icons/fa";
import Navbar from "../components/sub-comp/Navbar.jsx";
import Footer from "../components/sub-comp/MainFooter.jsx";
import FloatingSocialPanels from "../components/sub-comp/FloatingSocialPanels";

export default function Admission() {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    schoolName: "",
    completionYear: "",
    program: "",
    shortCourse: "Basic Computer Application",
    additionalInfo: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [counters, setCounters] = useState({ students: 0, regions: 0, graduates: 0 });
  
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  const programs = [
    {
      name: "Cyber Security",
      slug: "cyber-security",
      description: "Master the latest technologies and industry practices in cyber security"
    },
    {
      name: "Travel and Tourism",
      slug: "travel-tourism",
      description: "Master the latest technologies and industry practices in travel and tourism"
    },
    {
      name: "Information Technology",
      slug: "information-technology",
      description: "Master the latest technologies and industry practices in information technology"
    },
    {
      name: "Business Administration",
      slug: "business-administration",
      description: "Master the latest technologies and industry practices in business administration"
    },
    {
      name: "Electronics And Telecommunication Engineering",
      slug: "electronics-telecommunication",
      description: "Master the latest technologies and industry practices in electronics and telecommunication engineering"
    },
  ];

  const shortCourses = [
    "Basic Computer Application",
    "Graphics Design",
    "Web Design and Development",
    "Computer Maintenance and Repair (A+)",
    "Networking N+",
    "Computer Programming",
    "Others",
  ];

  const testimonials = [
    {
      name: "Sarah Mwangi",
      role: "Computer Science Graduate",
      image: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
      quote: "JRIIT transformed my career. The practical approach and industry connections opened doors I never imagined possible.",
      rating: 5
    },
    {
      name: "David Kimani",
      role: "Business Administration Student",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
      quote: "The supportive environment and real-world projects prepared me for success. I'm confident about my future.",
      rating: 5
    },
    {
      name: "Grace Ochieng",
      role: "Cyber Security Graduate",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
      quote: "JRIIT's cutting-edge curriculum and expert faculty gave me the skills to excel in the digital security field.",
      rating: 5
    }
  ];

  const stats = [
    { icon: FaGraduationCap, number: 391, label: "Undergraduate Students", color: "from-blue-500 to-purple-600" },
    { icon: FaGlobe, number: 25, label: "Tanzania Regions", color: "from-green-500 to-teal-600" },
    { icon: FaTrophy, number: 1411, label: "Graduate Students", color: "from-orange-500 to-red-600" }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [testimonials.length]);

  useEffect(() => {
    const animateCounters = () => {
      const duration = 2000;
      const steps = 60;
      const increment = 391 / steps;
      const incrementRegions = 25 / steps;
      const incrementGraduates = 1411 / steps;

      let currentStep = 0;
      const timer = setInterval(() => {
        currentStep++;
        setCounters({
          students: Math.min(Math.round(increment * currentStep), 391),
          regions: Math.min(Math.round(incrementRegions * currentStep), 25),
          graduates: Math.min(Math.round(incrementGraduates * currentStep), 1411)
        });

        if (currentStep >= steps) {
          clearInterval(timer);
        }
      }, duration / steps);

      return () => clearInterval(timer);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animateCounters();
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.5 }
    );

    const statsSection = document.getElementById('stats-section');
    if (statsSection) {
      observer.observe(statsSection);
    }

    return () => observer.disconnect();
  }, []);

  const validateForm = () => {
    const errors = {};
    
    if (!formData.firstName.trim()) errors.firstName = "First name is required";
    if (!formData.lastName.trim()) errors.lastName = "Last name is required";
    if (!formData.phone.trim()) errors.phone = "Phone number is required";
    if (!formData.schoolName.trim()) errors.schoolName = "School name is required";
    if (!formData.completionYear.trim()) errors.completionYear = "Completion year is required";
    if (!formData.program) errors.program = "Please select a program";
    if (!formData.additionalInfo.trim()) errors.additionalInfo = "Additional information is required";
    
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Create formatted email body
      const emailBody = `
        <!DOCTYPE html>
        <html>
        <head>
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; background: #f4f4f4; }
            .container { max-width: 650px; margin: 0 auto; background: white; }
            .header { 
              background: linear-gradient(135deg, #9333ea 0%, #3b82f6 100%); 
              color: white; 
              padding: 40px 30px; 
              text-align: center; 
            }
            .header h1 { margin: 0; font-size: 28px; }
            .header p { margin: 10px 0 0 0; opacity: 0.9; }
            .content { padding: 30px; }
            .section { 
              margin-bottom: 25px; 
              padding: 20px;
              background: #f9fafb;
              border-radius: 8px;
              border-left: 4px solid #9333ea;
            }
            .section-title { 
              font-weight: bold; 
              color: #9333ea; 
              margin-bottom: 15px;
              font-size: 18px;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            .field { 
              margin-bottom: 12px;
              padding: 10px 0;
              border-bottom: 1px solid #e5e7eb;
            }
            .field:last-child { border-bottom: none; }
            .label { 
              font-weight: 600; 
              color: #4b5563; 
              display: inline-block;
              min-width: 140px;
            }
            .value { 
              color: #1f2937;
              word-wrap: break-word;
            }
            .footer { 
              background: #1f2937; 
              color: white; 
              padding: 25px; 
              text-align: center; 
              font-size: 13px; 
            }
            .footer p { margin: 5px 0; }
            .badge {
              display: inline-block;
              background: #9333ea;
              color: white;
              padding: 5px 15px;
              border-radius: 20px;
              font-size: 12px;
              font-weight: bold;
              margin-top: 5px;
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h1>🎓 New Admission Application</h1>
              <p>JRIIT College - Admissions Office</p>
            </div>
            
            <div class="content">
              <div class="section">
                <div class="section-title">📋 Personal Information</div>
                <div class="field">
                  <span class="label">Full Name:</span>
                  <span class="value">${formData.firstName} ${formData.lastName}</span>
                </div>
                <div class="field">
                  <span class="label">Phone Number:</span>
                  <span class="value">${formData.phone}</span>
                </div>
                <div class="field">
                  <span class="label">Email Address:</span>
                  <span class="value">${formData.email || 'Not provided'}</span>
                </div>
              </div>

              <div class="section">
                <div class="section-title">🎓 Education Background</div>
                <div class="field">
                  <span class="label">School Name:</span>
                  <span class="value">${formData.schoolName}</span>
                </div>
                <div class="field">
                  <span class="label">Year Completed:</span>
                  <span class="value">${formData.completionYear}</span>
                </div>
              </div>

              <div class="section">
                <div class="section-title">📚 Program Selection</div>
                <div class="field">
                  <span class="label">Selected Program:</span>
                  <span class="value">
                    ${formData.program}
                    <span class="badge">PRIMARY PROGRAM</span>
                  </span>
                </div>
                <div class="field">
                  <span class="label">Short Course:</span>
                  <span class="value">${formData.shortCourse}</span>
                </div>
              </div>

              <div class="section">
                <div class="section-title">💬 Additional Information</div>
                <div class="value" style="line-height: 1.8;">
                  ${formData.additionalInfo.replace(/\n/g, '<br>')}
                </div>
              </div>
            </div>

            <div class="footer">
              <p><strong>JRIIT College Admissions Office</strong></p>
              <p>Sakina Area, Arusha Tanzania | P.O. Box 7071 Namanga Road</p>
              <p>📞 +255 754 360 590 | 📧 admissions@jriit.ac.tz</p>
              <p style="margin-top: 15px; opacity: 0.8;">
                Application received on: ${new Date().toLocaleString('en-US', {
        dateStyle: 'full',
        timeStyle: 'long',
        timeZone: 'Africa/Dar_es_Salaam'
      })}
              </p>
            </div>
          </div>
        </body>
        </html>
      `;

      // Send email using our custom API
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const cleanURL = API_BASE_URL.replace(/\/$/, ''); // Remove trailing slash
      
      const response = await fetch(`${cleanURL}/api/send-admission`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subject: `${formData.firstName} ${formData.lastName} - ${formData.program}`,
          body: emailBody,
          replyTo: formData.email,
          replyToName: `${formData.firstName} ${formData.lastName}`
        })
      });

      const result = await response.json();
      console.log('Admission email response:', result);

      if (result.success) {
        setSubmitSuccess(true);
        
        // Reset form after success
        setTimeout(() => {
          setSubmitSuccess(false);
          setFormData({
            firstName: "",
            lastName: "",
            phone: "",
            email: "",
            schoolName: "",
            completionYear: "",
            program: "",
            shortCourse: "Basic Computer Application",
            additionalInfo: "",
          });
          setCurrentStep(1);
        }, 3000);
      } else {
        console.error("Admission email failed:", result);
        alert("There was an error submitting your application. Please try again or contact us directly.");
      }
      
    } catch (error) {
      console.error("Submission error:", error.response?.data || error.message);
      alert("There was an error submitting your application. Please try again or contact us at principal@jriit.ac.tz");
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextStep = () => {
    if (currentStep < 4) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <Navbar />
      
      {/* Hero Section */}
      <motion.section 
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{ y, opacity }}
      >
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-blue-900/20 to-indigo-900/20" />
          <div className="absolute inset-0 opacity-30" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundRepeat: 'repeat'
          }} />
          
          <motion.div
            animate={{ 
              y: [0, -20, 0],
              rotate: [0, 5, 0]
            }}
            transition={{ 
              duration: 6, 
              repeat: Infinity, 
              ease: "easeInOut" 
            }}
            className="absolute top-20 left-20 w-32 h-32 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full blur-xl"
          />
          <motion.div
            animate={{ 
              y: [0, 20, 0],
              rotate: [0, -5, 0]
            }}
            transition={{ 
              duration: 8, 
              repeat: Infinity, 
              ease: "easeInOut" 
            }}
            className="absolute bottom-20 right-20 w-40 h-40 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-full blur-xl"
          />
        </div>

        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-8"
          >
            <motion.h1 
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.2 }}
            >
              Your Future
              <span className="block bg-gradient-to-r from-gold via-yellow-400 to-orange-400 bg-clip-text text-transparent">
                Starts Here
              </span>
            </motion.h1>
            
            <motion.p 
              className="text-lg sm:text-xl md:text-2xl text-gray-300 mb-6 sm:mb-8 max-w-3xl mx-auto leading-relaxed px-4"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.4 }}
            >
              Transform your dreams into reality at JRIIT. Join a community of innovators, 
              creators, and future leaders. Your journey to excellence begins with one bold step.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center px-4"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="group bg-gradient-to-r from-gold to-orange-500 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-semibold shadow-2xl hover:shadow-gold/25 transition-all duration-300 flex items-center justify-center gap-2 w-full sm:w-auto min-h-[44px]"
                onClick={() => document.getElementById('application-form').scrollIntoView({ behavior: 'smooth' })}
              >
                Start Your Application
                <FaArrowRight className="group-hover:translate-x-1 transition-transform duration-300" />
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white/30 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-white/10 transition-all duration-300 backdrop-blur-sm w-full sm:w-auto min-h-[44px]"
                onClick={() => document.getElementById('programs-section').scrollIntoView({ behavior: 'smooth' })}
              >
                Explore Programs
              </motion.button>
            </motion.div>
          </motion.div>
        </div>

        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-1 h-3 bg-white/60 rounded-full mt-2"
            />
          </div>
        </motion.div>
      </motion.section>

      {/* Statistics Section */}
      <section id="stats-section" className="relative py-20 bg-gradient-to-r from-purple-900/50 to-blue-900/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-16 px-4"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
              Why Choose JRIIT?
            </h2>
            <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto">
              Join thousands of successful graduates who transformed their lives through quality education
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 px-4">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="relative group"
              >
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 sm:p-8 text-center border border-white/20 hover:border-white/40 transition-all duration-300 hover:scale-105">
                  <div className={`w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 rounded-full bg-gradient-to-r ${stat.color} flex items-center justify-center text-white text-2xl sm:text-3xl shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <stat.icon />
                  </div>
                  <motion.div
                    className="text-4xl sm:text-5xl font-bold text-white mb-2"
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                  >
                    {stat.icon === FaGlobe ? counters.regions : 
                     stat.icon === FaGraduationCap ? counters.students : 
                     counters.graduates}
                  </motion.div>
                  <p className="text-gray-300 text-base sm:text-lg">{stat.label}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose JRIIT Section */}
      <section className="py-20 bg-gradient-to-br from-slate-800 to-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-16 px-4"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
              Why Choose JRIIT?
            </h2>
            <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto">
              Discover what makes JRIIT the premier choice for your educational journey
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 px-4">
            {[
              {
                icon: FaLightbulb,
                title: "Innovation First",
                description: "Cutting-edge curriculum designed for the digital age",
                color: "from-blue-500 to-cyan-500"
              },
              {
                icon: FaUsers,
                title: "Expert Faculty",
                description: "Learn from industry professionals and academic leaders",
                color: "from-purple-500 to-pink-500"
              },
              {
                icon: FaAward,
                title: "Industry Recognition",
                description: "Accredited programs with strong industry partnerships",
                color: "from-green-500 to-teal-500"
              },
              {
                icon: FaChartLine,
                title: "Career Growth",
                description: "95% employment rate with competitive starting salaries",
                color: "from-orange-500 to-red-500"
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
                className="group"
              >
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-white/20 hover:border-gold/50 transition-all duration-300 h-full">
                  <div className={`w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-r ${feature.color} rounded-2xl flex items-center justify-center text-white text-xl sm:text-2xl mb-4 sm:mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <feature.icon />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4 group-hover:text-gold transition-colors duration-300">
                    {feature.title}
                  </h3>
                  <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section id="programs-section" className="py-20 bg-gradient-to-br from-purple-900/50 to-blue-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-16 px-4"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
              Transform Your Future
            </h2>
            <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto">
              Choose from our comprehensive range of programs designed to prepare you for the digital age
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 px-4">
            {programs.map((program, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
                className="group cursor-pointer"
              >
                <div className="bg-gradient-to-br from-purple-900/50 to-blue-900/50 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-white/20 hover:border-gold/50 transition-all duration-300 h-full">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-r from-gold to-orange-500 rounded-2xl flex items-center justify-center text-white text-xl sm:text-2xl mb-4 sm:mb-6 group-hover:scale-110 transition-transform duration-300">
                    <FaRocket />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4 group-hover:text-gold transition-colors duration-300">
                    {program.name}
                  </h3>
                  <p className="text-gray-300 text-sm sm:text-base mb-4 sm:mb-6 leading-relaxed">
                    {program.description}
                  </p>
                  <a 
                    href={`/programs/${program.slug}`}
                    className="inline-flex items-center text-gold font-semibold group-hover:translate-x-2 transition-transform duration-300 cursor-pointer"
                  >
                    Learn More
                    <FaArrowRight className="ml-2" />
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gradient-to-br from-blue-900/50 to-purple-900/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-16 px-4"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
              Success Stories
            </h2>
            <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto">
              Hear from our graduates about their transformative journey at JRIIT
            </p>
          </motion.div>

          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTestimonial}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.5 }}
                className="bg-white/10 backdrop-blur-md rounded-3xl p-6 sm:p-8 md:p-12 border border-white/20"
              >
                <div className="text-center">
                  <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-6 sm:mb-8 rounded-full overflow-hidden border-4 border-gold">
                    <img 
                      src={testimonials[activeTestimonial].image} 
                      alt={testimonials[activeTestimonial].name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  <div className="flex justify-center mb-4 sm:mb-6">
                    {[...Array(testimonials[activeTestimonial].rating)].map((_, i) => (
                      <FaStar key={i} className="text-gold text-xl sm:text-2xl mx-1" />
                    ))}
                  </div>
                  
                  <blockquote className="text-lg sm:text-xl md:text-2xl text-white mb-6 sm:mb-8 italic leading-relaxed px-4">
                    "{testimonials[activeTestimonial].quote}"
                  </blockquote>
                  
                  <div>
                    <h4 className="text-lg sm:text-xl font-bold text-white mb-2">
                      {testimonials[activeTestimonial].name}
                    </h4>
                    <p className="text-gold font-semibold text-sm sm:text-base">
                      {testimonials[activeTestimonial].role}
                    </p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="flex justify-center mt-8 gap-3">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${index === activeTestimonial
                      ? 'bg-gold scale-125' 
                      : 'bg-white/30 hover:bg-white/50'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Information Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 to-purple-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-16 px-4"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
              Get in Touch
            </h2>
            <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto">
              Have questions? Our admissions team is here to help you every step of the way
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 px-4">
            {[
              {
                icon: FaPhone,
                title: "Call Us",
                info: "+255 754 360 590",
                description: "Speak directly with our admissions team"
              },
              {
                icon: FaEnvelope,
                title: "Email Us",
                info: "admissions@jriit.ac.tz",
                description: "Send us your questions and inquiries"
              },
              {
                icon: FaMapMarkerAlt,
                title: "Visit Us",
                info: "Sakina Area, Arusha",
                description: "Schedule a campus tour and meet our team"
              }
            ].map((contact, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
                className="group cursor-pointer"
              >
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-white/20 hover:border-gold/50 transition-all duration-300 text-center">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-r from-gold to-orange-500 rounded-2xl flex items-center justify-center text-white text-xl sm:text-2xl mx-auto mb-4 sm:mb-6 group-hover:scale-110 transition-transform duration-300">
                    <contact.icon />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-white mb-2 group-hover:text-gold transition-colors duration-300">
                    {contact.title}
                  </h3>
                  <p className="text-gold font-semibold text-base sm:text-lg mb-2">
                    {contact.info}
                  </p>
                  <p className="text-gray-300 text-xs sm:text-sm">
                    {contact.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form Section */}
      <section 
        id="application-form" 
        ref={containerRef}
        className="py-20 bg-gradient-to-br from-purple-900/50 to-slate-900"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-16 px-4"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
              Ready to Begin Your Journey?
            </h2>
            <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto">
              Take the first step towards your future. Our streamlined application process makes it easy to get started.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-white/10 backdrop-blur-md rounded-3xl p-6 sm:p-8 md:p-12 border border-white/20 shadow-2xl"
          >
            {/* Progress Steps */}
            <div className="flex justify-between mb-8 sm:mb-12 px-2">
              {[1, 2, 3, 4].map((step) => (
                <div key={step} className="flex flex-col items-center">
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-base sm:text-lg font-bold transition-all duration-300 ${step <= currentStep
                      ? 'bg-gradient-to-r from-gold to-orange-500 text-white' 
                      : 'bg-white/20 text-gray-400'
                  }`}>
                    {step < currentStep ? <FaCheckCircle /> : step}
                  </div>
                  <span className={`text-xs sm:text-sm mt-2 transition-colors duration-300 text-center ${step <= currentStep ? 'text-white' : 'text-gray-400'
                  }`}>
                    {step === 1 ? 'Personal' : step === 2 ? 'Contact' : step === 3 ? 'Education' : 'Program'}
                  </span>
                </div>
              ))}
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Step 1: Personal Information */}
              {currentStep === 1 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                  className="space-y-6"
                >
                  <h3 className="text-2xl font-bold text-white mb-6">Personal Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-white font-medium">First Name *</label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        className={`w-full px-3 sm:px-4 py-3 sm:py-4 bg-white/10 border rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-gold/20 transition-all duration-300 min-h-[44px] ${formErrors.firstName
                            ? 'border-red-400 focus:border-red-400' 
                            : 'border-white/20 focus:border-gold'
                        }`}
                        placeholder="Enter your first name"
                        required
                      />
                      {formErrors.firstName && (
                        <p className="text-red-400 text-sm mt-1">{formErrors.firstName}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <label className="text-white font-medium">Last Name *</label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        className={`w-full px-3 sm:px-4 py-3 sm:py-4 bg-white/10 border rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-gold/20 transition-all duration-300 min-h-[44px] ${formErrors.lastName
                            ? 'border-red-400 focus:border-red-400' 
                            : 'border-white/20 focus:border-gold'
                        }`}
                        placeholder="Enter your last name"
                        required
                      />
                      {formErrors.lastName && (
                        <p className="text-red-400 text-sm mt-1">{formErrors.lastName}</p>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 2: Contact Information */}
              {currentStep === 2 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                  className="space-y-6"
                >
                  <h3 className="text-2xl font-bold text-white mb-6">Contact Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-white font-medium">Phone Number *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 bg-white/10 border rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-gold/20 transition-all duration-300 ${formErrors.phone
                          ? 'border-red-400 focus:border-red-400'
                          : 'border-white/20 focus:border-gold'
                          }`}
                        placeholder="Enter your phone number"
                        required
                      />
                      {formErrors.phone && (
                        <p className="text-red-400 text-sm mt-1">{formErrors.phone}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <label className="text-white font-medium">Email Address</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 bg-white/10 border rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-gold/20 transition-all duration-300 ${formErrors.email
                          ? 'border-red-400 focus:border-red-400'
                          : 'border-white/20 focus:border-gold'
                          }`}
                        placeholder="Enter your email address"
                      />
                      {formErrors.email && (
                        <p className="text-red-400 text-sm mt-1">{formErrors.email}</p>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 3: Education Background */}
              {currentStep === 3 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                  className="space-y-6"
                >
                  <h3 className="text-2xl font-bold text-white mb-6">Education Background</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-white font-medium">O' Level Secondary School *</label>
                      <input
                        type="text"
                        name="schoolName"
                        value={formData.schoolName}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 bg-white/10 border rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-gold/20 transition-all duration-300 ${formErrors.schoolName
                          ? 'border-red-400 focus:border-red-400'
                          : 'border-white/20 focus:border-gold'
                          }`}
                        placeholder="Enter school name"
                        required
                      />
                      {formErrors.schoolName && (
                        <p className="text-red-400 text-sm mt-1">{formErrors.schoolName}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <label className="text-white font-medium">Year Completed *</label>
                      <input
                        type="text"
                        name="completionYear"
                        value={formData.completionYear}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 bg-white/10 border rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-gold/20 transition-all duration-300 ${formErrors.completionYear
                          ? 'border-red-400 focus:border-red-400'
                          : 'border-white/20 focus:border-gold'
                          }`}
                        placeholder="Enter completion year"
                        required
                      />
                      {formErrors.completionYear && (
                        <p className="text-red-400 text-sm mt-1">{formErrors.completionYear}</p>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 4: Program Selection */}
              {currentStep === 4 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                  className="space-y-6"
                >
                  <h3 className="text-2xl font-bold text-white mb-6">Program Selection</h3>
                  
                  <div className="space-y-4">
                    <label className="text-white font-medium">Select Programme *</label>
                    <div className="grid grid-cols-1 gap-3">
                      {programs.map((prog) => (
                        <label key={prog.name} className="flex items-center p-4 bg-white/10 border border-white/20 rounded-xl cursor-pointer hover:border-gold/50 transition-all duration-300">
                          <input
                            type="radio"
                            name="program"
                            value={prog.name}
                            checked={formData.program === prog.name}
                            onChange={handleChange}
                            className="w-5 h-5 text-gold focus:ring-gold border-white/30"
                          />
                          <span className="ml-3 text-white">{prog.name}</span>
                        </label>
                      ))}
                    </div>
                    {formErrors.program && (
                      <p className="text-red-400 text-sm mt-1">{formErrors.program}</p>
                    )}
                  </div>

                  <div className="space-y-4">
                    <label className="text-white font-medium">Select Short Course</label>
                    <select
                      name="shortCourse"
                      value={formData.shortCourse}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:border-gold focus:ring-2 focus:ring-gold/20 transition-all duration-300"
                    >
                      {shortCourses.map((course) => (
                        <option key={course} value={course} className="bg-slate-800 text-white">
                          {course}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-4">
                    <label className="text-white font-medium">Additional Information *</label>
                    <textarea
                      name="additionalInfo"
                      value={formData.additionalInfo}
                      onChange={handleChange}
                      rows={4}
                      className={`w-full px-4 py-3 bg-white/10 border rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-gold/20 transition-all duration-300 resize-none ${formErrors.additionalInfo
                        ? 'border-red-400 focus:border-red-400'
                        : 'border-white/20 focus:border-gold'
                        }`}
                      placeholder="Tell us more about your goals and interests..."
                      required
                    />
                    {formErrors.additionalInfo && (
                      <p className="text-red-400 text-sm mt-1">{formErrors.additionalInfo}</p>
                    )}
                  </div>
                </motion.div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between pt-8">
                {currentStep > 1 && (
                  <motion.button
                    type="button"
                    onClick={prevStep}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-8 py-3 border-2 border-white/30 text-white rounded-xl hover:bg-white/10 transition-all duration-300"
                  >
                    Previous
                  </motion.button>
                )}
                
                {currentStep < 4 ? (
                  <motion.button
                    type="button"
                    onClick={nextStep}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="ml-auto px-8 py-3 bg-gradient-to-r from-gold to-orange-500 text-white rounded-xl hover:shadow-lg hover:shadow-gold/25 transition-all duration-300"
                  >
                    Next
                  </motion.button>
                ) : (
                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    whileHover={{ scale: isSubmitting ? 1 : 1.05 }}
                    whileTap={{ scale: isSubmitting ? 1 : 0.95 }}
                    className={`ml-auto px-8 py-3 rounded-xl transition-all duration-300 flex items-center gap-2 ${isSubmitting
                        ? 'bg-gray-500 cursor-not-allowed' 
                        : 'bg-gradient-to-r from-gold to-orange-500 hover:shadow-lg hover:shadow-gold/25'
                    } text-white`}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Submitting...
                      </>
                    ) : submitSuccess ? (
                      <>
                        <FaCheckCircle className="text-green-400" />
                        Submitted Successfully!
                      </>
                    ) : (
                      <>
                        Submit Application
                        <FaRocket className="ml-2" />
                      </>
                    )}
                  </motion.button>
                )}
              </div>
            </form>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-900 to-blue-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="px-4"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-6">
              Ready to Transform Your Future?
            </h2>
            <p className="text-lg sm:text-xl text-gray-300 mb-6 sm:mb-8 max-w-3xl mx-auto">
              Join thousands of students who have already taken the first step towards their dreams. 
              Your success story starts here.
            </p>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-gradient-to-r from-gold to-orange-500 text-white px-6 sm:px-10 py-3 sm:py-4 rounded-full text-lg sm:text-xl font-semibold shadow-2xl hover:shadow-gold/25 transition-all duration-300 flex items-center justify-center gap-2 sm:gap-3 mx-auto w-full sm:w-auto min-h-[44px]"
              onClick={() => document.getElementById('application-form').scrollIntoView({ behavior: 'smooth' })}
            >
              <FaHandshake className="text-xl sm:text-2xl" />
              Start Your Application Today
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Floating Action Button */}
      <motion.div
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 1 }}
        className="fixed bottom-4 sm:bottom-8 right-4 sm:right-8 z-50"
      >
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className="bg-gradient-to-r from-gold to-orange-500 text-white p-3 sm:p-4 rounded-full shadow-2xl hover:shadow-gold/25 transition-all duration-300 min-w-[44px] min-h-[44px] flex items-center justify-center"
          onClick={() => document.getElementById('application-form').scrollIntoView({ behavior: 'smooth' })}
        >
          <FaRocket className="text-xl sm:text-2xl" />
        </motion.button>
      </motion.div>

      {/* Success Modal */}
      <AnimatePresence>
        {submitSuccess && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
              className="bg-white rounded-3xl p-8 max-w-md w-full text-center shadow-2xl"
            >
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <FaCheckCircle className="text-6xl text-green-500" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Application Submitted!
              </h3>
              <p className="text-gray-600 mb-6">
                Thank you for your interest in JRIIT! We've received your application and will be in touch soon.
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-gold to-orange-500 text-white px-6 py-3 rounded-xl font-semibold"
                onClick={() => setSubmitSuccess(false)}
              >
                Continue
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
      <FloatingSocialPanels />
    </div>
  );
}

